import React, { useMemo, useState, useEffect } from 'react';
import {
  Modal,
  useModal,
  useConfirmModal,
} from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import {
  Box,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { FileDescriptionIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import messages from './messages';
import useIntl from '../../../hooks/useIntl';
import { SingleSelect } from '@fortress-technology-solutions/fortress-component-library/Molecules';

const BudgetTypeForm = ({ open, onClose, onGenerate, generating, value }) => {
  const intl = useIntl();
  const [budgetType, setBudgetType] = useState('');

  const BUDGET_TYPES = useMemo(
    () => [
      { value: '', text: intl.formatMessage(messages.chooseBudgetType) },
      {
        value: 'INITIAL',
        text: intl.formatMessage(messages.budgetTypeInitial),
      },
      {
        value: 'REGULAR_REPORT',
        text: intl.formatMessage(messages.budgetTypeRegular),
      },
      {
        value: 'RENT_CHANGE',
        text: intl.formatMessage(messages.budgetTypeRentChange),
      },
      { value: 'SMR', text: intl.formatMessage(messages.budgetTypeSmr) },
      {
        value: 'OTHER_SERVICING',
        text: intl.formatMessage(messages.budgetTypeOther),
      },
    ],
    [intl],
  );

  useEffect(() => {
    if (open) {
      setBudgetType(value);
    }
  }, [open, value]);

  const handleGenerate = () => {
    if (budgetType) onGenerate(budgetType);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      scroll="body"
      ModalProps={{
        PaperProps: {
          sx: {
            width: { xs: 'calc(100% - 32px)', sm: 480 },
            maxHeight: 'calc(100vh - 100px)',
          },
        },
      }}
      icon={<FileDescriptionIcon />}
      title={intl.formatMessage(messages.create35607Title)}
      actionsProps={[
        {
          children: intl.formatMessage(messages.cancel),
          variant: 'text',
          onClick: onClose,
          disabled: generating,
        },
        {
          children: intl.formatMessage(messages.generate),
          variant: 'primary',
          onClick: handleGenerate,
          disabled: !budgetType || generating,
          //TODO: move this to CL before merge this PR
          sx: {
            width: '106px',
            height: '40px',
            backgroundColor: !budgetType ? '#B4B4B4' : '#5B7CE8',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: !budgetType ? '#B4B4B4' : '#4A6FE7',
            },
            '&:disabled': {
              backgroundColor: '#B4B4B4',
              color: '#FFFFFF',
            },
          },
        },
      ]}
    >
      <Typography mb={3}>
        {intl.formatMessage(messages.description, { reportName: '3560-7' })}
      </Typography>

      <Box sx={{ mt: 2 }}>
        <SingleSelect
          fullWidth
          value={budgetType}
          onChange={(val) => setBudgetType(val)}
          options={BUDGET_TYPES}
          label={intl.formatMessage(messages.budgetTypeLabel)}
          placeholder={intl.formatMessage(messages.chooseBudgetType)}
          //TODO: move this to CL before merge this PR
          InputLabelProps={{
            sx: {
              color: 'text.primary',
              '&.Mui-focused, &.MuiFormLabel-filled, &.MuiInputLabel-shrink, &:hover':
                {
                  color: 'text.primary',
                },
            },
          }}
        />
      </Box>
    </Modal>
  );
};

export const useBudgetTypeModal = ({ onGenerate, generating, value }) => {
  const { open, openModal, closeModal } = useModal();
  const { openConfirm, openConfirmModal, closeConfirmModal } =
    useConfirmModal();

  const budgetTypeModalNode = (
    <BudgetTypeForm
      open={open}
      onClose={closeModal}
      value={value}
      generating={generating}
      onGenerate={(val) => {
        onGenerate(val);
      }}
    />
  );

  return {
    openBudgetTypeModal: openModal,
    budgetTypeModalNode,
    openConfirm,
    openConfirmModal,
    closeConfirmModal,
    closeModal,
  };
};
